import React, { useState } from 'react';
import { getSourceStyle, truncate } from './chat-utils';
import { FEEDBACK_OPTIONS } from './chat-constants';

/**
 * Display a single chat message with sources, feedback, and suggested questions
 */
export function Message({ msg, onSuggestedQuestion, onFeedback }) {
  const isUser = msg.role === 'user';
  const [feedback, setFeedback] = useState(msg.feedback_sent ? msg.feedback_value : null);

  const handleFeedback = async (value) => {
    setFeedback(value);
    if (onFeedback) {
      await onFeedback(msg.id, { feedback: value });
    }
  };

  return (
    <div className="chat-message" style={{ alignItems: isUser ? 'flex-end' : 'flex-start' }}>
      {/* Avatar label */}
      <div className="chat-message-label" style={{ paddingLeft: isUser ? 0 : 2 }}>
        {isUser ? 'You' : '🏠 PropAtlas Advisor'}
      </div>

      {/* Message bubble */}
      <div className={`chat-message-bubble ${isUser ? 'user' : 'assistant'}`}>
        {msg.content}
      </div>

      {/* Sources */}
      {!isUser && msg.sources?.length > 0 && (
        <div className="chat-sources">
          {msg.sources.map((s, i) => (
            <SourceChip key={i} source={s} />
          ))}
        </div>
      )}

      {/* Professional advice disclaimer */}
      {!isUser && msg.requires_professional_advice && (
        <div className="chat-disclaimer">
          <span>⚠️</span>
          <span>
            General information only — not personal advice. Consult a qualified{' '}
            {msg.professional_type || 'professional'} if you need guidance for your situation.
          </span>
        </div>
      )}

      {/* Feedback (assistant messages only) */}
      {!isUser && (
        <div className="chat-feedback-prompt">
          <div style={{ marginBottom: 8 }}>Was this helpful?</div>
          <div className="chat-feedback-buttons">
            {FEEDBACK_OPTIONS.map(option => (
              <button
                key={option.value}
                className={`chat-feedback-btn ${feedback === option.value ? 'active' : ''}`}
                onClick={() => handleFeedback(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Suggested questions */}
      {!isUser && msg.suggested_questions?.length > 0 && (
        <div className="chat-suggested-questions">
          {msg.suggested_questions.map((q, i) => (
            <SuggestedQ
              key={i}
              text={q}
              onClick={() => onSuggestedQuestion?.(q)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Display a source citation with link
 */
function SourceChip({ source }) {
  const style = getSourceStyle(source);
  const inner = (
    <span
      className="chat-source-chip"
      style={{
        background: style.bg,
        color: style.color,
        border: `1px solid ${style.color}33`,
      }}
    >
      {source.name}
      {source.topic && (
        <span style={{ opacity: 0.7, fontWeight: 400 }}>· {truncate(source.topic, 20)}</span>
      )}
    </span>
  );

  return source.url ? (
    <a href={source.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
      {inner}
    </a>
  ) : (
    inner
  );
}

/**
 * Display a suggested question as clickable button
 */
function SuggestedQ({ text, onClick }) {
  return (
    <button className="chat-suggested-btn" onClick={() => onClick?.(text)}>
      {truncate(text, 30)}
    </button>
  );
}
